/* eslint-disable @typescript-eslint/indent */
import { Injectable } from "@angular/core";
import { Validators } from "@angular/forms";
import { isNil } from "lodash";
import * as moment from "moment";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Operation } from "src/app/shared/types/entity.type";
import { PaginatedResponse } from "../../../../core/http/paginated-response.type";
import { DataTableParamsWhere } from "../../../../shared/components/data-table/data-table.type";
import { QuestionBase } from "../../../../shared/components/dynamic-form/question-base";
import { CheckboxQuestion } from "../../../../shared/components/dynamic-form/question-checkbox";
import { DateQuestion } from "../../../../shared/components/dynamic-form/question-date";
import { DropdownOption, DropdownQuestion } from "../../../../shared/components/dynamic-form/question-dropdown";
import { LinkQuestion } from "../../../../shared/components/dynamic-form/question-link";
import { NumberQuestion } from "../../../../shared/components/dynamic-form/question-number";
import { TextAreaQuestion } from "../../../../shared/components/dynamic-form/question-textarea";
import { TextboxQuestion } from "../../../../shared/components/dynamic-form/question-textbox";
import { pattern } from "../../../../shared/constants/patterns";
import { AnyObject } from "../../../../shared/types/app.type";
import { UserInfo } from "../../../../shared/types/user-info";
import { setFilterCondition } from "../../../../shared/utils/helpers/common.helper";
import { CompanyService } from "../../companies/company.service";
import { Company } from "../../companies/company.type";
import { ConfigurableOptionService } from "../../configurable-options/configurable-options.service";
import { ConfigurableOption } from "../../configurable-options/configurable-options.type";
import { ContractService } from "../../contracts/contract.service";
import { Contract } from "../../contracts/contract.type";
import { InternalProviderService } from "../../internal-provider/internal-provider.service";
import { InternalProvider } from "../../internal-provider/internal-provider.type";
import { LocationService } from "../../locations/location.service";
import { Location } from "../../locations/location.type";
import { PartnerService } from "../../partners/partner.service";
import { Partner, PartnerSpecialTypes } from "../../partners/partner.type";
import { Ticket } from "../../tickets/ticket.type";
import { getAssetCategories, getAssetCoverageStatuses, getAssetQuotedTerms } from "../asset";
import { AssetService } from "../asset.service";
import { AssetServiceUI } from "../asset.service.ui";
import { Asset, AssetCoverageStatuses } from "../asset.type";

@Injectable()
export class CreateAssetService {
  constructor(
    private partnerService: PartnerService,
    private companyService: CompanyService,
    private locationService: LocationService,
    private contractService: ContractService,
    private internalProviderService: InternalProviderService,
    private configurableOptionService: ConfigurableOptionService,
    private assetServiceUI: AssetServiceUI,
    private assetService: AssetService,
  ) {}

  getPartners(query?: { name: string }): Observable<DropdownOption<Partner>[]> {
    return this.partnerService
      .getPartners({ where: setFilterCondition(query, "$cont") })
      .pipe(
        map((r: PaginatedResponse<Partner>) => r.items.map((p: Partner) => ({ value: p.id, label: p.name, data: p }))),
      );
  }

  getPartner(partnerId: string): Observable<DropdownOption[]> {
    return this.partnerService.getPartner(partnerId).pipe(map((r: Partner) => [{ value: r.id, label: r.name }]));
  }

  getCompanies(query?: { partnerId?: string; name?: string }): Observable<DropdownOption[]> {
    const { partnerId, ...filters } = query;
    return this.companyService
      .getCompanies({ where: { partnerId, ...setFilterCondition(filters, "$cont") } })
      .pipe(map((r: PaginatedResponse<Company>) => r.items.map((c: Company) => ({ value: c.id, label: c.name }))));
  }

  getCompany(companyId: string): Observable<DropdownOption[]> {
    return this.companyService.getCompany(companyId).pipe(map((c: Company) => [{ value: c.id, label: c.name }]));
  }

  getContracts(query?: { companyId: string; isChannelWorks?: boolean; name?: string }): Observable<DropdownOption[]> {
    const { companyId, isChannelWorks, ...filters } = query;
    return this.contractService
      .getContracts({
        where: {
          companyId,
          ...(!isNil(isChannelWorks) && { maintenanceProvider: { isChannelWorks } }),
          ...setFilterCondition(filters, "$cont"),
        } as DataTableParamsWhere<Contract>,
      })
      .pipe(
        map((r: PaginatedResponse<Contract>) => r.items.map((c: Contract) => ({ value: c.id, label: `${c.name}` }))),
      );
  }

  getLocations(query?: { companyId: string; name?: string }): Observable<DropdownOption[]> {
    const { companyId, ...filters } = query;
    return this.locationService
      .getLocations({ where: { companyId, ...setFilterCondition(filters, "$cont") } })
      .pipe(
        map((r: PaginatedResponse<Location>) => r.items.map((l: Location) => ({ value: l.id, label: `${l.name}` }))),
      );
  }

  getInternalProviders(query?: { name?: string }): Observable<DropdownOption[]> {
    return this.internalProviderService.getInternalProviders({ where: setFilterCondition(query, "$cont") }).pipe(
      map((data: PaginatedResponse<InternalProvider>) =>
        data.items.map((internalProvider: InternalProvider) => ({
          value: internalProvider.id,
          label: internalProvider.name,
        })),
      ),
    );
  }

  getSlas(query?: { id?: string; value?: string }): Observable<DropdownOption[]> {
    const { id, ...filters } = query;
    return this.configurableOptionService
      .getConfigurableOptions({ where: { id, ...setFilterCondition(filters, "$cont") } })
      .pipe(
        map((r: PaginatedResponse<ConfigurableOption>) =>
          r.items.map((co: ConfigurableOption) => ({ value: co.id, label: co.value })),
        ),
      );
  }

  createContractNumberQuestion(options: { contract: Contract; isUpdating?: boolean }): NumberQuestion {
    return new LinkQuestion({
      value: options?.contract?.numberVersion || `${options?.contract?.number}-${options?.contract?.version}`,
      key: "contractNumber",
      label: "Contract #",
      required: false,
      hide: !options?.contract,
      link: options.contract?.id ? `${this.contractService.path}/${options.contract?.id}` : "",
      copyToClipboard: options?.isUpdating,
      disabled: true,
      order: 12,
    });
  }

  getOem(oemId: string): Observable<DropdownOption[]> {
    return this.assetService.getAssetOEM(oemId).pipe(map((oem) => [{ value: oem.externalId, label: oem.name }]));
  }

  getOems(query?: { name: string }): Observable<DropdownOption[]> {
    return this.assetService
      .getAssetOEMs({ where: query })
      .pipe(map((r) => r.items.map((oem) => ({ value: oem.externalId, label: oem.name }))));
  }

  getModel(modelId: string): Observable<DropdownOption[]> {
    return this.assetService
      .getAssetModel(modelId)
      .pipe(map((model) => [{ value: model.externalId, label: model.name }]));
  }

  getModels(query?: { oemExternalId: number; name?: string }): Observable<DropdownOption[]> {
    const { oemExternalId, ...filters } = query;
    return this.assetService
      .getAssetModels({ where: { oemExternalId, ...filters } })
      .pipe(map((r) => r.items.map((model) => ({ value: model.externalId, label: model.name }))));
  }

  getType(typeId: string): Observable<DropdownOption[]> {
    return this.assetService.getAssetType(typeId).pipe(map((type) => [{ value: type.externalId, label: type.name }]));
  }

  getTypes(query?: { oemExternalId: number; modelExternalId: number; name?: string }): Observable<DropdownOption[]> {
    const { oemExternalId, modelExternalId, ...filters } = query;
    return this.assetService
      .getAssetTypes({ where: { oemExternalId, modelExternalId, ...filters } })
      .pipe(map((r) => r.items.map((type) => ({ value: type.externalId, label: type.name }))));
  }

  createDateQuestion(options?: {
    asset?: Asset;
    startDateEdit?: boolean;
    coverageStatusValue?: string;
    resetValue?: boolean;
    hide?: boolean;
    isUpdating?: boolean;
  }): DateQuestion {
    const dateQuestion = new DateQuestion({
      value: !options?.resetValue && options.asset?.startDate,
      key: "startDate",
      label: "Start Date",
      required: true,
      disabled: !options.startDateEdit,
      copyToClipboard: options?.isUpdating,
      order: 14,
      hide: options.hide,
    });

    return dateQuestion;
  }

  generateQuestions(options: {
    asset?: Asset;
    partnerValue?: string;
    companyValue?: string;
    isChannelWorksValue?: boolean;
    companyOptions?: DropdownOption[];
    companyEdit?: boolean;
    companySkip?: boolean;
    companyHidden?: boolean;
    contractHidden?: boolean;
    coverageStatusHidden?: boolean;
    startDateHidden?: boolean;
    endDateHidden?: boolean;
    partnerEdit?: boolean;
    partnerSkip?: boolean;
    partnerPriceHidden?: boolean;
    clientPriceHidden?: boolean;
    currentUser?: UserInfo;
    onlyEdit?: string[];
    nameEdit?: boolean;
    serialNumberEdit?: boolean;
    descriptionEdit?: boolean;
    typeEdit?: boolean;
    coverageStatusEdit?: boolean;
    startDateEdit?: boolean;
    endDateEdit?: boolean;
    slaEdit?: boolean;
    modelEdit?: boolean;
    oemEdit?: boolean;
    contract?: Contract;
    contractIdEdit?: boolean;
    partnerPriceEdit?: boolean;
    clientPriceEdit?: boolean;
    locationIdEdit?: boolean;
    channelWorksCostEdit?: boolean;
    coverageStatusValue?: AssetCoverageStatuses;
    hideContractNumber?: boolean;
    hideQuotedSla?: boolean;
    hideQuotedPrice?: boolean;
    hideQuotedTerm?: boolean;
    quotedSlaEdit?: boolean;
    quotedPriceEdit?: boolean;
    quotedTermEdit?: boolean;
    monitoringEdit?: boolean;
    dmrEdit?: boolean;
    dmrHidden?: boolean;
    slaHidden?: boolean;
    monitoringHidden?: boolean;
    channelWorksCostHidden?: boolean;
    internalProviderHidden?: boolean;
    internalProviderEdit?: boolean;
    showFmvValue?: boolean;
    showBookValue?: boolean;
    fmvValueEdit?: boolean;
    bookValueEdit?: boolean;
    operation?: Operation;
  }): Observable<QuestionBase<any>[]> {
    // set default values to true when they are not defined
    options = {
      companyEdit: true,
      partnerEdit: true,
      nameEdit: true,
      serialNumberEdit: true,
      descriptionEdit: true,
      typeEdit: true,
      coverageStatusEdit: true,
      startDateEdit: true,
      endDateEdit: true,
      slaEdit: true,
      modelEdit: true,
      oemEdit: true,
      channelWorksCostEdit: true,
      contractIdEdit: true,
      partnerPriceEdit: true,
      clientPriceEdit: true,
      locationIdEdit: true,
      quotedSlaEdit: true,
      quotedPriceEdit: true,
      quotedTermEdit: true,
      monitoringEdit: true,
      dmrEdit: true,
      internalProviderEdit: true,
      ...options,
    };

    const isUpdating = options.operation === Operation.update;

    const questions: QuestionBase<any>[] = [
      // Name 1
      new TextboxQuestion({
        value: options.asset?.name,
        key: "name",
        label: "Name",
        required: true,
        disabled: !options.nameEdit,
        copyToClipboard: isUpdating,
        order: 1,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Name is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(2),
            message: "Name is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(200),
            message: "Name is <strong>too long</strong>",
          },
        ],
      }),

      // Serial Number 2
      new TextboxQuestion({
        value: options.asset?.serialNumber,
        key: "serialNumber",
        label: "Serial #",
        required: true,
        disabled: !options.serialNumberEdit,
        copyToClipboard: isUpdating,
        order: 2,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Serial # is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Serial # is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Serial # is <strong>too long</strong>",
          },
          {
            key: "naPattern",
            validator: Validators.pattern(pattern.na),
            message: "N/A is <strong>not valid value</strong>",
          },
        ],
      }),

      // Description 3
      new TextAreaQuestion({
        value: options.asset?.description,
        key: "description",
        label: "Description",
        disabled: !options.descriptionEdit,
        required: true,
        copyToClipboard: isUpdating,
        order: 3,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Description is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Description is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(5000),
            message: "Description is <strong>too long</strong>",
          },
        ],
      }),

      // Category 4
      new DropdownQuestion({
        value: options.asset?.category,
        key: "category",
        label: "Category",
        options: getAssetCategories(),
        disabled: !options.typeEdit,
        copyToClipboard: isUpdating,
        order: 4,
        required: true,
      }),

      // OEM 5
      new DropdownQuestion({
        value: options.asset?.type?.model?.oem?.externalId,
        key: "oemExternalId",
        label: "OEM",
        required: true,
        order: 5,
        ignoreInSubmit: true,
        currentOption: {
          value: options.asset?.type?.model?.oem?.externalId,
          label: options.asset?.type?.model?.oem?.name,
        },
        asyncOptions: this.getOems(),
        search: {
          filter: {
            callback: ({ value: name }) => this.getOems({ name }),
          },
        },
        addNewEntry: {
          action: (form, dropdown) => {
            const newEntry = this.assetServiceUI.showEquipmentIdentifiersCreationModal(this.assetService);
            newEntry.afterClosed().subscribe((data) => {
              if (data) {
                dropdown.options = [...dropdown.options, { value: data.oemExternalId, label: data.oemName }];
                form.controls["oemExternalId"].setValue(data.oemExternalId);
                form.controls["modelExternalId"].setValue(data.modelExternalId);
                form.controls["typeExternalId"].setValue(data.typeExternalId);
                form.controls["typeExternalId"].markAsDirty();
              }
            });
          },
        },
      }),

      // Model # 6
      new DropdownQuestion({
        value: options.asset?.type?.model.externalId,
        key: "modelExternalId",
        label: "Model #",
        required: true,
        order: 6,
        copyToClipboard: isUpdating,
        ignoreInSubmit: true,
        currentOption: {
          value: options.asset?.type?.model?.externalId,
          label: options.asset?.type?.model?.name,
        },
        dependsOn: {
          key: "oemExternalId",
          callback: ({ parentValue: oemExternalId }) => {
            return this.getModels({ oemExternalId: +oemExternalId });
          },
        },
        search: {
          filter: {
            parentKey: "oemExternalId",
            callback: ({ parentValue: oemExternalId, value: name }) =>
              this.getModels({ oemExternalId: +oemExternalId, name }),
          },
        },
        addNewEntry: {
          action: (form, dropdown) => {
            const oemOptions = (dropdown.questions.find((qs) => qs.key === "oemExternalId") as DropdownQuestion)
              .options;
            const oem = oemOptions.find((opt) => opt.value === form.value.oemExternalId);
            const newEntry = this.assetServiceUI.showEquipmentIdentifiersCreationModal(this.assetService, oem);
            newEntry.afterClosed().subscribe((data) => {
              if (data) {
                dropdown.options = [...dropdown.options, { value: data.modelExternalId, label: data.modelName }];
                form.controls["modelExternalId"].setValue(data.modelExternalId);
                form.controls["typeExternalId"].setValue(data.typeExternalId);
                form.controls["typeExternalId"].markAsDirty();
              }
            });
          },
        },
      }),

      // Type 7
      new DropdownQuestion({
        value: options.asset?.type?.externalId,
        key: "typeExternalId",
        label: "Type",
        required: true,
        order: 7,
        copyToClipboard: isUpdating,
        currentOption: {
          value: options.asset?.type?.externalId,
          label: options.asset?.type?.name,
        },
        dependsOn: {
          key: "modelExternalId",
          callback: ({ parentValue: modelExternalId, form }) =>
            this.getTypes({
              // the only way to get oemExternalId is through the form
              oemExternalId: +form.controls["oemExternalId"].value,
              modelExternalId: +modelExternalId,
            }),
        },
        search: {
          filter: {
            parentKey: "modelExternalId",
            callback: ({ parentValue: modelExternalId, value: name, form }) =>
              this.getTypes({
                // the only way to get oemExternalId is through the form
                oemExternalId: +form.controls["oemExternalId"].value,
                modelExternalId: +modelExternalId,
                name,
              }),
          },
        },
        addNewEntry: {
          action: (form, dropdown) => {
            const oemOptions = (dropdown.questions.find((qs) => qs.key === "oemExternalId") as DropdownQuestion)
              .options;
            const oem = oemOptions.find((opt) => opt.value === form.value.oemExternalId);

            const modelOptions = (dropdown.questions.find((qs) => qs.key === "modelExternalId") as DropdownQuestion)
              .options;
            const model = modelOptions.find((opt) => opt.value === form.value.modelExternalId);

            const newEntry = this.assetServiceUI.showEquipmentIdentifiersCreationModal(this.assetService, oem, model);
            newEntry.afterClosed().subscribe((data) => {
              if (data) {
                dropdown.options = [...dropdown.options, { value: data.typeExternalId, label: data.typeName }];
                form.controls["typeExternalId"].setValue(data.typeExternalId);
                form.controls["typeExternalId"].markAsDirty();
              }
            });
          },
        },
      }),

      // Partner 8
      !options?.partnerSkip &&
        new DropdownQuestion({
          value: options.currentUser.partnerId || options?.partnerValue || options.asset?.company?.partnerId,
          key: "partnerId",
          label: "Partner",
          currentOption: {
            label: options.asset?.company?.partner?.name,
            value: options.asset?.company?.partner?.id,
          },
          ignoreInSubmit: true,
          disabled: !options?.partnerEdit,
          asyncOptions: options.partnerValue ? this.getPartner(options.partnerValue) : this.getPartners(),
          search: {
            filter: {
              callback: ({ value: name }) => this.getPartners({ name }),
            },
          },
          required: true,
          copyToClipboard: isUpdating,
          order: options.asset ? 2 : 8,
        }),

      // Company 9
      !options?.companySkip &&
        new DropdownQuestion({
          value: options?.companyValue || options.asset?.companyId,
          key: "companyId",
          label: "Company",
          required: true,
          currentOption: {
            label: options.asset?.company?.name,
            value: options.asset?.company?.id,
          },
          disabled: !options?.companyEdit,
          hide: options.companyHidden,
          options: options?.companyOptions,
          ignoreInSubmit: options.currentUser.isCompany(),
          ...(!options?.companyOptions && {
            asyncOptions: options?.companyValue
              ? this.getCompany(options.companyValue)
              : options?.currentUser.partnerId &&
                this.getCompanies({
                  ...(!options.currentUser.hasManyCompanies() && { partnerId: options?.currentUser.partnerId }),
                }),
          }),
          dependsOn: {
            key: "partnerId",
            callback: ({ parentValue: partnerId }) =>
              options?.companyValue
                ? this.getCompany(options.companyValue)
                : this.getCompanies({ ...(!options.currentUser.hasManyCompanies() && { partnerId }) }),
          },
          search: !options?.companyOptions && {
            filter: {
              parentKey: "partnerId",
              callback: ({ parentValue: partnerId, value: name }) =>
                this.getCompanies({ ...(!options.currentUser.hasManyCompanies() && { partnerId }), name }),
            },
          },
          copyToClipboard: isUpdating,
          order: options.asset ? 2 : 9,
        }),

      // Location 10
      new DropdownQuestion({
        value: options.asset?.locationId,
        key: "locationId",
        label: "Location",
        currentOption: {
          label: options.asset?.location?.name,
          value: options.asset?.location?.id,
        },
        asyncOptions: options?.companyValue && this.getLocations({ companyId: options.companyValue }),
        disabled: !options.locationIdEdit,
        copyToClipboard: isUpdating,
        order: 10,
        showNullOption: true,
        dependsOn: {
          key: "companyId",
          callback: ({ parentValue: companyId }) => this.getLocations({ companyId }),
        },
        search: {
          filter: {
            parentKey: "companyId",
            callback: ({ parentValue: companyId, value: name }) => this.getLocations({ companyId, name }),
          },
        },
      }),

      // Contract 11
      new DropdownQuestion({
        value: options.asset?.contractId || options.contract?.id,
        key: "contractId",
        label: "Contract",
        currentOption: {
          label: options.asset?.contract?.name,
          value: options.asset?.contract?.id,
        },
        showNullOption: true,
        hide: (options.contractHidden || !!options.contract) ?? false,
        asyncOptions:
          options?.companyValue &&
          this.getContracts({
            companyId: options.companyValue,
            ...this.getIsChannelWorksFilter(options.currentUser, options.asset?.contractId, options.contractIdEdit),
          }),
        disabled: !options.contractIdEdit,
        copyToClipboard: isUpdating,
        order: 11,
        dependsOn: {
          key: "companyId",
          callback: ({ parentValue: companyId }) =>
            this.getContracts({
              companyId: companyId || options.asset?.companyId,
              ...this.getIsChannelWorksFilter(options.currentUser, options.asset?.contractId, options.contractIdEdit),
            }),
        },
        search: {
          filter: {
            parentKey: "companyId",
            callback: ({ parentValue: companyId, value: name }) =>
              this.getContracts({
                companyId: companyId || options.asset?.companyId,
                name,
                ...this.getIsChannelWorksFilter(options.currentUser, options.asset?.contractId, options.contractIdEdit),
              }),
          },
        },
      }),

      // Contract Number 12
      !options?.hideContractNumber &&
        this.createContractNumberQuestion({
          contract: options?.asset.contract,
          isUpdating,
        }),

      // Coverage Status 13
      new DropdownQuestion({
        value: options.asset?.coverageStatus,
        key: "coverageStatus",
        label: "Asset Coverage Status",
        options: getAssetCoverageStatuses(),
        disabled: !options.coverageStatusEdit,
        hide: options.coverageStatusHidden || !!options.contract,
        copyToClipboard: isUpdating,
        order: 13,
      }),

      // Start Date 14
      this.createDateQuestion({
        asset: options?.asset,
        startDateEdit: options?.startDateEdit,
        coverageStatusValue: options?.coverageStatusValue,
        hide: options.startDateHidden,
        isUpdating: isUpdating,
      }),

      // End Date 15
      new DateQuestion({
        value: options.asset?.endDate,
        key: "endDate",
        label: "End Date",
        required: true,
        minDate: moment(options.asset?.startDate).toDate(),
        disabled: !(options.asset?.startDate && options.endDateEdit),
        copyToClipboard: isUpdating,
        order: 15,
        hide: options.endDateHidden,
      }),

      // CW Cost 16
      new NumberQuestion({
        value: options.asset?.channelWorksCost === 0 ? "0" : options.asset?.channelWorksCost,
        key: "channelWorksCost",
        type: "text",
        label: "CW Cost",
        placeholder: "0",
        required: false,
        hide: !options.currentUser.isGlobal() || options.channelWorksCostHidden,
        disabled: !options.channelWorksCostEdit,
        isCurrency: true,
        copyToClipboard: isUpdating,
        order: 16,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.price),
            message: "CW Cost is <strong>not valid</strong>",
          },
        ],
      }),

      // Partner Price 17
      new NumberQuestion({
        value: options.asset?.partnerPrice === 0 ? "0" : options.asset?.partnerPrice,
        key: "partnerPrice",
        type: "text",
        label: "Partner Price",
        placeholder: "0",
        required: false,
        disabled: !options.partnerPriceEdit,
        hide: options.partnerPriceHidden ?? true,
        isCurrency: true,
        copyToClipboard: isUpdating,
        order: 17,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.price),
            message: "Partner price is <strong>not valid</strong>",
          },
        ],
      }),

      // Client Price 18
      new NumberQuestion({
        value: options.asset?.clientPrice === 0 ? "0" : options.asset?.clientPrice,
        key: "clientPrice",
        type: "text",
        label: "Client Price",
        placeholder: "0",
        required: false,
        disabled: !options.clientPriceEdit,
        hide: options.clientPriceHidden ?? true,
        isCurrency: true,
        copyToClipboard: isUpdating,
        order: 18,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.price),
            message: "Client price is <strong>not valid</strong>",
          },
        ],
      }),

      // Buy Price 19
      new NumberQuestion({
        value: options.asset?.buyPrice === 0 ? "0" : options.asset?.buyPrice,
        key: "buyPrice",
        type: "text",
        label: "Buy Price",
        placeholder: "0",
        required: false,
        disabled: true,
        hide: !(isUpdating && options.asset?.company?.partner?.specialType === PartnerSpecialTypes.Relutech),
        isCurrency: true,
        copyToClipboard: isUpdating,
        order: 19,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.price),
            message: "Buy price is <strong>not valid</strong>",
          },
        ],
      }),

      // Sla 20
      new DropdownQuestion({
        value: options.asset?.slaId,
        key: "slaId",
        label: "SLA",
        currentOption: {
          label: options.asset?.sla?.value,
          value: options.asset?.sla?.id,
        },
        asyncOptions: this.getSlas({ id: options.asset?.sla?.id }),
        showNullOption: true,
        disabled: !options.slaEdit,
        search: {
          filter: {
            callback: ({ value }) => this.getSlas({ value }),
          },
        },
        hide: options.slaHidden,
        copyToClipboard: isUpdating,
        order: 20,
      }),

      // Internal Provider 21
      options.currentUser.isGlobal() &&
        new DropdownQuestion({
          value: options.asset?.internalProviderId,
          key: "internalProviderId",
          label: "Internal Provider",
          currentOption: {
            label: options.asset?.internalProvider?.name,
            value: options.asset?.internalProvider?.id,
          },
          tooltipEnabled: true,
          asyncOptions: this.getInternalProviders(),
          showNullOption: true,
          hide: options.internalProviderHidden,
          disabled: !options.internalProviderEdit,
          search: {
            filter: {
              callback: ({ value: name }) => this.getInternalProviders({ name }),
            },
          },
          copyToClipboard: isUpdating,
          order: 21,
        }),

      // Quoted SLA 22
      new TextAreaQuestion({
        value: options.asset?.quotedSla,
        key: "quotedSla",
        label: "Quoted SLA",
        copyToClipboard: isUpdating,
        order: 22,
        hide: options.hideQuotedSla,
        disabled: !options.quotedSlaEdit,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.words),
            message: "Quoted SLA is <strong>not valid</strong>",
          },
          {
            key: "minlength",
            validator: Validators.minLength(1),
            message: "Quoted SLA is <strong>too short</strong>",
          },
          {
            key: "maxlength",
            validator: Validators.maxLength(255),
            message: "Quoted SLA is <strong>too long</strong>",
          },
        ],
      }),

      // Quoted Price 23
      new NumberQuestion({
        value: options.asset?.quotedPrice === 0 ? "0" : options.asset?.quotedPrice,
        key: "quotedPrice",
        type: "text",
        label: "Quoted Price",
        placeholder: "0",
        isCurrency: true,
        hide: options.hideQuotedPrice,
        disabled: !options.quotedPriceEdit,
        copyToClipboard: isUpdating,
        order: 23,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.price),
            message: "Quoted price is <strong>not valid</strong>",
          },
        ],
      }),

      // Fmv Value 24
      new NumberQuestion({
        value: options.asset?.fairMarketValue === 0 ? "0" : options.asset?.fairMarketValue,
        key: "fairMarketValue",
        type: "text",
        label: "FMV",
        placeholder: "0",
        isCurrency: true,
        hide: !options.showFmvValue,
        disabled: !options.fmvValueEdit,
        copyToClipboard: isUpdating,
        order: 24,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.fmvAndBookValues),
            message: "FMV value is <strong>not valid</strong>",
          },
        ],
      }),

      // Book Value 25
      new NumberQuestion({
        value: options.asset?.bookValue === 0 ? "0" : options.asset?.bookValue,
        key: "bookValue",
        type: "text",
        label: "Book Value",
        placeholder: "0",
        isCurrency: true,
        hide: !options.showBookValue,
        disabled: !options.bookValueEdit,
        copyToClipboard: isUpdating,
        order: 25,
        errors: [
          {
            key: "pattern",
            validator: Validators.pattern(pattern.fmvAndBookValues),
            message: "Book value is <strong>not valid</strong>",
          },
        ],
      }),

      // Quoted Term 26
      new DropdownQuestion({
        value: options.asset?.quotedTerm,
        key: "quotedTerm",
        label: "Quoted Term",
        options: getAssetQuotedTerms(),
        hide: options.hideQuotedTerm,
        disabled: !options.quotedTermEdit,
        copyToClipboard: isUpdating,
        order: 26,
      }),

      // Configuration Notation 27
      new CheckboxQuestion({
        value: options.asset?.hasConfigurationNotes,
        key: "hasConfigurationNotes",
        label: "Configuration",
        hide: !options.currentUser.isGlobal(),
        order: 27,
      }),

      // Monitoring 28
      new CheckboxQuestion({
        value: options.asset?.monitoring,
        key: "monitoring",
        label: "Monitoring",
        disabled: !options.monitoringEdit,
        hide: options.monitoringHidden,
        order: 28,
      }),

      // DMR 29
      new CheckboxQuestion({
        value: options.asset?.dmr,
        key: "dmr",
        label: "DMR",
        disabled: !options.dmrEdit,
        hide: options.dmrHidden,
        order: 29,
      }),
    ];

    return of(questions.filter((field) => field).sort((a, b) => a.order - b.order));
  }

  isPartnerPriceVisible(currentUser: UserInfo, company: Company, contract: Contract): boolean {
    const globalUserCondition =
      currentUser.isGlobal() &&
      (contract?.maintenanceProvider?.isChannelWorks ||
        (!contract?.maintenanceProvider?.isChannelWorks && company.canManageClientPrice));

    const partnerUserCondition =
      currentUser.isPartnerOrViewAsPartner() && contract?.maintenanceProvider?.isChannelWorks;

    return globalUserCondition || partnerUserCondition;
  }

  isClientPriceVisible(currentUser: UserInfo, company: Company, contract: Contract): boolean {
    return (
      (!!contract && company.canManageClientPrice) ||
      (currentUser.isGlobal() &&
        !!contract &&
        !company.canManageClientPrice &&
        contract?.maintenanceProvider?.isChannelWorks)
    );
  }

  isPartnerPriceEnabled(currentUser: UserInfo, company: Company, contract: Contract): boolean {
    return (
      currentUser.isGlobal() &&
      (contract?.maintenanceProvider?.isChannelWorks ||
        (!contract?.maintenanceProvider?.isChannelWorks && company.canManageClientPrice))
    );
  }

  isClientPriceEnabled(currentUser: UserInfo, company: Company, contract: Contract): boolean {
    return (
      (!!contract &&
        company.canManageClientPrice &&
        (!currentUser.isCompanyOrViewAsCompany() ||
          (currentUser.isCompanyOrViewAsCompany() && !contract.maintenanceProvider?.isChannelWorks))) ||
      (currentUser.isGlobal() &&
        !!contract &&
        !company.canManageClientPrice &&
        contract?.maintenanceProvider?.isChannelWorks)
    );
  }

  isSerialNumberEnabled(currentUser: UserInfo, disabledContractType: boolean, relatedTickets: Ticket[]): boolean {
    const isEnabled = currentUser.isGlobalAdmin();
    return isEnabled && !disabledContractType && !relatedTickets.length;
  }

  isFieldEnabled(currentUser: UserInfo, contract: Contract): boolean {
    const isEnabled = !(currentUser.isPartnerOrViewAsPartner() && contract?.maintenanceProvider?.isChannelWorks);
    return isEnabled;
  }

  allowLoadChannelWorksContract(currentUser: UserInfo, contractIdEdit: boolean): boolean {
    const allowLoad = currentUser.isGlobal() || (!currentUser.isGlobal() && !contractIdEdit);
    return allowLoad;
  }

  getIsChannelWorksFilter(currentUser: UserInfo, contractId: string, contractIdEdit: boolean): AnyObject {
    if (
      (contractId && !this.allowLoadChannelWorksContract(currentUser, contractIdEdit)) ||
      (!contractId && !currentUser.isGlobal())
    ) {
      return {
        isChannelWorks: false,
      };
    }
    return {};
  }
}
